import { useEffect, useRef, useState } from 'react';

interface LeaveGuardOptions {
  shouldWarn: boolean;
}

interface LeaveGuardResult {
  leaveModalOpen: boolean;
  handleConfirmLeaveNavigation: () => void;
  handleCloseLeaveModal: () => void;
  allowNextNavigationWithoutWarning: () => void;
}

function useLeaveGuard(options: LeaveGuardOptions): LeaveGuardResult {
  const { shouldWarn } = options;

  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const pendingHrefRef = useRef<string | null>(null);
  const isPatchingHistoryRef = useRef(false);
  const skipBeforeUnloadRef = useRef(false);
  const ignoreNextNavigationRef = useRef(false);

  // Warn on browser tab close / refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!shouldWarn || skipBeforeUnloadRef.current) return;

      event.preventDefault();
      // Chrome requires returnValue to be set
      // eslint-disable-next-line no-param-reassign
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldWarn]);

  // Block in-app navigation initiated via history.pushState / replaceState
  useEffect(() => {
    const historyObject = window.history as History & {
      originalPushState?: History['pushState'];
      originalReplaceState?: History['replaceState'];
    };

    if (!shouldWarn) {
      return;
    }

    if (isPatchingHistoryRef.current) {
      return;
    }

    const originalPushState =
      historyObject.originalPushState ?? historyObject.pushState.bind(historyObject);
    const originalReplaceState =
      historyObject.originalReplaceState ?? historyObject.replaceState.bind(historyObject);

    historyObject.originalPushState = originalPushState;
    historyObject.originalReplaceState = originalReplaceState;

    const handleNavigationAttempt = (targetUrl: string | null, navigate: () => void) => {
      if (!shouldWarn || ignoreNextNavigationRef.current) {
        ignoreNextNavigationRef.current = false;
        navigate();
        return;
      }

      // If there is already a pending navigation, ignore subsequent attempts
      // until the user decides in the confirmation modal.
      if (pendingHrefRef.current) {
        return;
      }

      if (!targetUrl) {
        navigate();
        return;
      }

      pendingHrefRef.current = targetUrl;
      setLeaveModalOpen(true);
    };

    const patchedPushState: History['pushState'] = (data, unused, url) => {
      const targetUrl = typeof url === 'string' ? url : url ? String(url) : null;

      handleNavigationAttempt(targetUrl, () => {
        originalPushState(data, unused, url);
      });
    };

    const patchedReplaceState: History['replaceState'] = (data, unused, url) => {
      const targetUrl = typeof url === 'string' ? url : url ? String(url) : null;

      handleNavigationAttempt(targetUrl, () => {
        originalReplaceState(data, unused, url);
      });
    };

    isPatchingHistoryRef.current = true;
    historyObject.pushState = patchedPushState;
    historyObject.replaceState = patchedReplaceState;

    return () => {
      historyObject.pushState = originalPushState;
      historyObject.replaceState = originalReplaceState;
      isPatchingHistoryRef.current = false;
    };
  }, [shouldWarn]);

  // If the warning is disabled while there is a pending navigation,
  // clear it and close the modal to avoid stale state.
  useEffect(() => {
    if (!shouldWarn) {
      pendingHrefRef.current = null;
      setLeaveModalOpen(false);
      skipBeforeUnloadRef.current = false;
      ignoreNextNavigationRef.current = false;
    }
  }, [shouldWarn]);

  const handleConfirmLeaveNavigation = () => {
    setLeaveModalOpen(false);

    if (pendingHrefRef.current) {
      const href = pendingHrefRef.current;
      pendingHrefRef.current = null;
      // Avoid showing the native beforeunload dialog when navigation was already confirmed
      skipBeforeUnloadRef.current = true;
      window.location.assign(href);
    }
  };

  const handleCloseLeaveModal = () => {
    setLeaveModalOpen(false);
    pendingHrefRef.current = null;
  };

  const allowNextNavigationWithoutWarning = () => {
    ignoreNextNavigationRef.current = true;
    skipBeforeUnloadRef.current = true;
  };

  return {
    leaveModalOpen,
    handleConfirmLeaveNavigation,
    handleCloseLeaveModal,
    allowNextNavigationWithoutWarning,
  };
}

export { useLeaveGuard };
